/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import {
  AutocompleteInput,
  DropdownProps,
  Icon,
  TRIGGER_INPUT_ID,
} from '@toggle/design-system';
import { keyboard, KeyboardKeys } from '@toggle/helpers/src/constants/keyboard';
import { useDisableScroll } from '@toggle/helpers/src/hooks/use-disable-scroll/useDisableScroll';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { FC, useEffect, useRef, useState } from 'react';

import { Company, CompanyWithReportingLabel } from '@/api/search/search-schema';
import { SearchItem } from '@/components/search/components/search-item/SearchItem';
import { useTouchDevice } from '@/stores/MediaQueryStore';
import { createLogGA } from '@/utils/analytics/logGA';

import { DynamicInputEnd } from './components/input-end';
import { Loader } from './components/loader/Loader';
import { MobileSearchButton } from './components/mobile-search-button/MobileSearchButton';
import { SearchChip, SearchChips } from './components/search-chips/SearchChips';
import { useSearchData } from './hooks/useSearchData';
import { useSearchHotkey } from './hooks/useSearchHotkey';
import * as S from './Search.styles';
import {
  formatAssetName,
  getCompanyIndexByAssetName,
} from './utils/company-utils/companyUtils';
import {
  createChipsData,
  getActiveChip,
  getFocusedChip,
  mapChipCompanies,
  mapSearchCompanies,
} from './utils/search-utils/searchUtils';

const logGA = createLogGA('Search');

export interface SearchProps {
  ticker?: string;
  isHeaderSearch?: boolean;
}

export const Search: FC<SearchProps> = ({
  isHeaderSearch = false,
  ticker = '',
}) => {
  const { t, i18n } = useTranslation('common');
  const { push, query } = useRouter();
  const queryAssetName =
    (query.asset as string | undefined)?.toLowerCase() || '';
  const locale =
    typeof window === 'undefined' ? i18n?.resolvedLanguage : navigator.language;
  const isTouchDevice = useTouchDevice();
  const [value, setValue] = useState('');
  const [chipsData, setChipsData] = useState<SearchChip[]>([]);
  const [activeChip, setActiveChip] = useState<null | SearchChip>(null);
  const mappedChipsCache = useRef<Record<string, CompanyWithReportingLabel[]>>(
    {}
  );
  const [showItems, setShowItems] = useState(false);
  const [isChipFocused, setIsChipFocused] = useState(false);

  const { companies, loading } = useSearchData(value);
  const location = isHeaderSearch ? 'assetPageHeader' : 'landingPage';

  const mappedSearchCompanies =
    value && companies?.length ? mapSearchCompanies(t, companies, locale) : [];
  const dropdownItems = value
    ? mappedSearchCompanies
    : activeChip
    ? mappedChipsCache.current[activeChip.label]
    : [];

  useDisableScroll(isHeaderSearch && showItems, 'auto');

  const activeCompanyIdx =
    isHeaderSearch && activeChip
      ? getCompanyIndexByAssetName(activeChip.companies, queryAssetName)
      : -1;

  const onActiveChipChange = (chip: SearchChip) => {
    setActiveChip(chip);
    if (!mappedChipsCache.current[chip.label]) {
      mappedChipsCache.current[chip.label] = mapChipCompanies(chip, locale);
    }
  };

  useEffect(() => {
    if (!value && companies?.length && !chipsData.length) {
      const chipsData = createChipsData(t, companies, i18n.resolvedLanguage);
      setChipsData(chipsData);
    }
  }, [companies, value]);

  useEffect(() => {
    if (chipsData.length) {
      onActiveChipChange(getActiveChip(chipsData, queryAssetName));
    }
  }, [chipsData, queryAssetName]);

  useEffect(() => {
    if (value && !loading) {
      logGA('type_search_bar', {
        userInput: value,
        location,
        assetNotFound: !companies?.length,
      });
    }
  }, [value, companies, loading]);

  const openSearch = () => {
    onItemsShowChange(true);
    setTimeout(() => {
      document.getElementById(TRIGGER_INPUT_ID)?.focus();
    }, 1);
  };

  useEffect(() => {
    document.addEventListener('openSearch', openSearch);
    return () => {
      document.removeEventListener('openSearch', openSearch);
    };
  }, []);

  useSearchHotkey({
    onPress: openSearch,
    location,
    companyPage: queryAssetName,
  });

  const onChange = (value: string) => {
    setValue(value);
    setIsChipFocused(false);
    if (!value) {
      onActiveChipChange(getActiveChip(chipsData, queryAssetName));
    }
  };

  const onChipClick = (chip: SearchChip) => {
    onActiveChipChange(chip);
    logGA('click_calendar_chip', {
      calendarChip: chip.label,
    });
  };

  const onClear = () => {
    logGA('click_clear_search', {
      userInput: value,
      location,
    });
    onChange('');
  };

  const onItemsShowChange = (show: boolean) => {
    setShowItems(show);
    setIsChipFocused(false);
    if (!show && chipsData.length) {
      onActiveChipChange(getActiveChip(chipsData, queryAssetName));
    }
  };

  const onSearchClose = () => {
    setValue('');
    onItemsShowChange(false);
  };

  const onOutsideClick = () => {
    setValue('');
  };

  const selectItem: DropdownProps<Company>['selectItem'] = (item, trigger) => {
    const assetName = formatAssetName(item);
    logGA('select_search_asset', {
      assetSearched: assetName,
      calendarChip: activeChip?.label || 'none',
      location,
    });
    setValue('');

    if (trigger === 'enter') {
      push(assetName);
    }
  };

  const onKeyDown = (key: KeyboardKeys) => {
    if (!value && key === 'Tab') {
      setIsChipFocused(prev => !prev);
      logGA('press_tab', {
        location,
        companyPage: queryAssetName,
        moveFocusTo: isChipFocused ? 'Chips' : 'Autocomplete List',
      });
    } else if (
      isChipFocused &&
      (key === keyboard.left.key || key === keyboard.right.key)
    ) {
      const focusedChip = getFocusedChip(
        chipsData,
        activeChip as SearchChip,
        key
      );
      logGA(
        key === keyboard.left.key ? 'press_left_arrow' : 'press_right_arrow',
        {
          location,
          companyPage: queryAssetName,
        }
      );
      onChipClick(focusedChip);
    } else if (key === keyboard.down.key || key === keyboard.up.key) {
      logGA(key === keyboard.down.key ? 'press_down_arrow' : 'press_up_arrow', {
        location,
        companyPage: queryAssetName,
      });
      setIsChipFocused(false);
    } else if (key === keyboard.esc.key) {
      logGA('press_esc', {
        location,
        companyPage: queryAssetName,
      });
      onSearchClose();
    } else if (key === keyboard.enter.key) {
      logGA('press_enter', {
        location,
        companyPage: queryAssetName,
      });
    }
  };

  const renderDropdownItem = (item: CompanyWithReportingLabel) => (
    <SearchItem data={item} searchValue={value} />
  );

  const isChipVisible = !value && !!activeChip;

  const afterInputNode = isChipVisible ? (
    <SearchChips
      isChipFocused={isChipFocused}
      active={activeChip}
      chips={chipsData}
      onClick={onChipClick}
    />
  ) : null;

  return (
    <>
      <MobileSearchButton
        isInHeader={isHeaderSearch}
        openSearch={openSearch}
        t={t}
      />
      <S.StyledDropdown<CompanyWithReportingLabel>
        shouldResetScrollOnItemsChange
        $isHeaderSearch={isHeaderSearch}
        $isChipsVisible={!loading && !value}
        onItemsShowChange={onItemsShowChange}
        selectItem={selectItem}
        dropdownItems={dropdownItems}
        renderDropdownItem={renderDropdownItem}
        shouldShowItems={showItems}
        focusedItemIdx={-1}
        activeItemIdx={activeCompanyIdx}
        shouldFocusFirstOnTab={isChipFocused || !!value}
        onKeyDown={onKeyDown}
        onOutsideClick={onOutsideClick}
        shouldCloseOnSelect
        shouldCloseOnOutsideClick={!isTouchDevice}
        shouldDisableItemMouseEvents={isTouchDevice}
        beforeResultsNode={
          loading ? (
            <Loader />
          ) : value ? (
            <S.ResultsPlaceholder
              noResults={!companies?.length}
              $isHeaderSearch={isHeaderSearch}
            >
              {t('searchResultsPlaceholder')}
            </S.ResultsPlaceholder>
          ) : (
            showItems && afterInputNode
          )
        }
        enableFloating={false}
      >
        <AutocompleteInput
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={ticker || t('searchPlaceholder')}
          leftIcon={<Icon iconName="Search" size={22} />}
          rightIcon={
            <DynamicInputEnd
              value={value}
              onClear={onClear}
              isListOpened={showItems}
              isTouchDevice={isTouchDevice}
              onSearchClose={onSearchClose}
              t={t}
            />
          }
        />
      </S.StyledDropdown>
      {isHeaderSearch && showItems && (
        <S.Overlay data-testid="search-overlay" />
      )}
    </>
  );
};
